/**
 * LE BOUTON DE LECTURE (design §3, brief §7).
 *
 * DEUX VOIX DERRIÈRE LE MÊME BOUTON, et le joueur n'a pas à savoir laquelle parle.
 *
 * 1. **La synthèse serveur** (§7) : trois profils par aventure, une bande fabriquée une fois pour
 *    le nœud et servie à tout le monde. C'est elle qu'on demande d'abord.
 * 2. **La voix du navigateur** : gratuite, hors ligne, sans quota, et toujours là. Elle prend le
 *    relais sans rien dire quand la première manque — pas configurée, quota du §8.1 épuisé,
 *    fabrication en cours ou en échec.
 *
 * On n'attend pas indéfiniment la première : deux tentatives, puis la seconde parle. Un bouton de
 * lecture qui tourne pendant huit secondes est un bouton qu'on ne presse plus.
 *
 * SANS AUCUNE DES DEUX, LE BOUTON N'EXISTE PAS. Un bouton qui ne fait rien ment, et c'est plus
 * grave qu'une fonction absente.
 *
 * LE MODE MAINS LIBRES EST LA SEULE CHOSE QUI TOURNE LES PAGES TOUTE SEULE, et il faut l'avoir armé
 * au pupitre. Il lit le nœud d'un bout à l'autre, tourne les feuillets au rythme de la voix, **et
 * s'arrête à l'amorce** : jamais un choix ne se prend tout seul. Éteint, le bouton lit le feuillet
 * affiché et se tait — c'est la règle du lot 4, qui reste vraie.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api/client.js";
import { bornesDesFeuillets, facteurDe, feuilletPourLInstant, useMainsLibres, useVitesse } from "../lecture/voix.js";
import { Glyphe } from "./Glyphe.js";
import { t } from "../lexique/langue.js";

/** Ce qu'il faut pour lire un NŒUD : de quoi demander sa bande, et de quoi tourner ses pages. Le
 * combat, lui, n'a rien de tout cela — il passe juste un texte. */
export interface Lecture {
  partieId: string;
  /** Le nœud courant : changer de nœud coupe la voix et jette la bande d'avant. */
  clef: string;
  feuillets: string[][];
  /** L'amorce n'est pas un feuillet, mais elle EST dans la bande : sans elle, les bornes seraient
   * calculées sur un texte plus court que le son, et les pages tourneraient trop tôt. */
  amorce: string | null;
  page: number;
  surPage: (page: number) => void;
}

type Etat = "muet" | "attente" | "parle" | "pause";

interface Bande {
  url: string;
  secondes: number;
}

function laSynthese(): SpeechSynthesis | null {
  return typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;
}

/** Existe-t-il une voix française ? La liste arrive de façon asynchrone sur certains navigateurs :
 * on s'abonne à son arrivée plutôt que de conclure trop tôt qu'il n'y en a pas. */
function useVoixFrancaise(): SpeechSynthesisVoice | null {
  const [voix, poseLaVoix] = useState<SpeechSynthesisVoice | null>(null);

  useEffect(() => {
    const synthese = laSynthese();
    if (!synthese) return;
    const cherche = () => {
      const trouvee = synthese.getVoices().find((v) => v.lang.toLowerCase().startsWith("fr"));
      poseLaVoix(trouvee ?? null);
    };
    cherche();
    synthese.addEventListener("voiceschanged", cherche);
    return () => synthese.removeEventListener("voiceschanged", cherche);
  }, []);

  return voix;
}

/** Combien de fois on redemande la bande avant de laisser parler le navigateur, et à quel rythme.
 * Deux secondes en tout : au-delà, on fait attendre quelqu'un devant un bouton. */
const RELANCES = 2;
const RELANCE_MS = 1000;

export function Voix({
  texte,
  titre = t("Lire à voix haute"),
  lecture
}: {
  /** Ce que la voix du navigateur dira : le titre et le feuillet affiché, jamais l'interface. */
  texte: string;
  titre?: string;
  lecture?: Lecture;
}) {
  const voixDuNavigateur = useVoixFrancaise();
  const [vitesse] = useVitesse();
  const [mainsLibres] = useMainsLibres();
  const [etat, poseLEtat] = useState<Etat>("muet");
  const [serveurMuet, poseServeurMuet] = useState(false);
  const bande = useRef<HTMLAudioElement | null>(null);

  const facteur = facteurDe(vitesse);

  const arrete = useCallback(() => {
    laSynthese()?.cancel();
    const son = bande.current;
    if (son) {
      // ON DÉBRANCHE AVANT D'ARRÊTER, et l'on ne vide SURTOUT pas `src`. Affecter une source vide
      // fait lever un événement `error` à l'élément : le repli se déclenchait alors tout seul, la
      // voix du navigateur repartait sur un texte qu'on venait d'interrompre, et le bouton restait
      // bloqué sur « Silence ». Trouvé dans un vrai navigateur, jamais dans un test.
      son.ontimeupdate = null;
      son.onended = null;
      son.onerror = null;
      son.pause();
      bande.current = null;
    }
    poseLEtat("muet");
  }, []);

  // CHANGER DE FEUILLET, DE NŒUD OU D'ÉCRAN COUPE LA VOIX. Sans cela, elle continuerait de lire un
  // texte qui n'est plus affiché — la seule chose pire qu'une voix qu'on ne peut pas arrêter. En
  // mains libres, c'est la voix qui tourne la page : on ne se coupe donc pas soi-même, et l'on ne
  // surveille que le NŒUD.
  useEffect(() => arrete, [lecture?.clef, arrete]);
  useEffect(() => {
    if (!mainsLibres) arrete();
  }, [texte, mainsLibres, arrete]);

  // La vitesse se règle EN ÉCOUTANT : un changement de cran s'applique à la bande en cours.
  useEffect(() => {
    if (bande.current) bande.current.playbackRate = facteur;
  }, [facteur]);

  if (!voixDuNavigateur && (serveurMuet || !lecture)) return null;

  /** La bande du nœud, si le serveur en a une — ou peut en faire une assez vite. */
  const demandeLaBande = async (): Promise<Bande | null> => {
    if (!lecture) return null;
    for (let essai = 0; essai <= RELANCES; essai++) {
      try {
        const reponse = await api.poste<{ voix: { etat: string; url: string | null; secondes: number | null } }>(
          `/parties/${lecture.partieId}/voix`,
          {}
        );
        const dit = reponse.voix;
        if (dit.etat === "pret" && dit.url) return { url: dit.url, secondes: dit.secondes ?? 0 };
        // « en cours » : quelqu'un la fabrique (peut-être nous). On repasse une fois ou deux.
        if (dit.etat !== "en_cours") break;
      } catch {
        // Quota épuisé, route absente, réseau coupé : la voix du navigateur ne demande rien à
        // personne, et c'est exactement pour ces cas-là qu'on l'a gardée.
        break;
      }
      if (essai < RELANCES) await new Promise((r) => setTimeout(r, RELANCE_MS));
    }
    return null;
  };

  const litLaBande = (trouvee: Bande) => {
    const feuillets = lecture!.feuillets;
    // Les bornes se calculent sur le texte ENTIER de la bande — feuillets ET amorce —, sans quoi
    // elles seraient étalées sur un texte plus court que le son.
    const pages = lecture!.amorce ? [...feuillets, [lecture!.amorce]] : feuillets;
    const bornes = bornesDesFeuillets(pages, trouvee.secondes);

    const son = new Audio(trouvee.url);
    son.playbackRate = facteur;
    // ON NE POSE PAS `currentTime` AVANT D'AVOIR LES MÉTADONNÉES. Tant que la durée est inconnue,
    // l'élément n'a pas de ligne de temps où se placer : selon le navigateur, l'affectation est
    // ignorée en silence ou elle lève. On attend l'événement — il arrive de toute façon avant que
    // le premier échantillon ne sorte.
    const debut = bornes[lecture!.page] ?? 0;
    if (debut > 0) son.addEventListener("loadedmetadata", () => (son.currentTime = debut), { once: true });

    const finDuFeuillet = bornes[lecture!.page + 1];
    son.ontimeupdate = () => {
      if (!mainsLibres) {
        // Sans mains libres, on ne lit QUE le feuillet affiché : on s'arrête à sa borne.
        if (finDuFeuillet !== undefined && son.currentTime >= finDuFeuillet) arrete();
        return;
      }
      const vise = feuilletPourLInstant(bornes, son.currentTime, lecture!.page);
      if (vise !== lecture!.page && vise < feuillets.length) lecture!.surPage(vise);
    };
    son.onended = arrete;
    son.onerror = () => {
      // Un fichier effacé, un répertoire déplacé : on ne laisse pas le joueur sans voix.
      bande.current = null;
      poseServeurMuet(true);
      if (voixDuNavigateur) litAuNavigateur();
      else poseLEtat("muet");
    };
    bande.current = son;
    poseLEtat("parle");
    // LA LECTURE PEUT ÊTRE REFUSÉE — un navigateur qui exige un geste de l'utilisateur, un onglet
    // en arrière-plan. On ne laisse pas la promesse se rejeter dans le vide : la voix du navigateur
    // prend le relais, comme dans tous les autres cas où la bande manque.
    son.play().catch(() => {
      bande.current = null;
      if (voixDuNavigateur) litAuNavigateur();
      else poseLEtat("muet");
    });
  };

  const litAuNavigateur = () => {
    const synthese = laSynthese();
    if (!synthese || !voixDuNavigateur) {
      poseLEtat("muet");
      return;
    }
    synthese.cancel();
    const propos = new SpeechSynthesisUtterance(texte);
    propos.voice = voixDuNavigateur;
    propos.lang = voixDuNavigateur.lang;
    propos.rate = facteur;
    propos.onend = () => {
      // MAINS LIBRES : on tourne la page, et l'effet de `texte` relancera la lecture. On s'arrête
      // au dernier feuillet — l'amorce se lit à l'œil, devant les choix, et c'est là qu'on décide.
      if (mainsLibres && lecture && lecture.page + 1 < lecture.feuillets.length) {
        lecture.surPage(lecture.page + 1);
        return;
      }
      poseLEtat("muet");
    };
    propos.onerror = () => poseLEtat("muet");
    synthese.speak(propos);
    poseLEtat("parle");
  };

  const lit = async () => {
    poseLEtat("attente");
    const trouvee = serveurMuet ? null : await demandeLaBande();
    if (trouvee) litLaBande(trouvee);
    else if (voixDuNavigateur) litAuNavigateur();
    else {
      // Ni l'une ni l'autre : on ne laisse pas le bouton tourner dans le vide.
      poseServeurMuet(true);
      poseLEtat("muet");
    }
  };

  const met = () => {
    if (bande.current) {
      bande.current.pause();
      poseLEtat("pause");
      return;
    }
    laSynthese()?.pause();
    poseLEtat("pause");
  };

  const reprend = () => {
    if (bande.current) void bande.current.play();
    else laSynthese()?.resume();
    poseLEtat("parle");
  };

  const parle = etat === "parle" || etat === "attente";

  return (
    <span className="rang rang--serre">
      <button
        type="button"
        className={parle ? "voix voix--parle" : "voix"}
        onClick={parle ? arrete : etat === "pause" ? reprend : () => void lit()}
        aria-pressed={parle}
        title={parle ? "Faire silence" : etat === "pause" ? "Reprendre" : titre}
      >
        <Glyphe nom={parle ? "silence" : "lire"} taille={16} />
        <span className="capitales">{etat === "attente" ? "…" : parle ? "Silence" : etat === "pause" ? "Reprendre" : "Lire"}</span>
      </button>

      {/* La pause n'apparaît QUE pendant la lecture : un bouton de pause sur une voix qui se tait
          est un bouton mort de plus dans la marge. */}
      {etat === "parle" ? (
        <button type="button" className="voix" onClick={met} title={t("Suspendre la lecture")}>
          <Glyphe nom="horloge" taille={14} />
          <span className="capitales">{t("Pause")}</span>
        </button>
      ) : null}

      {mainsLibres && lecture ? (
        <span className="capitales faible" title={t("Le mode mains libres tourne les feuillets tout seul, et s'arrête devant les choix.")}>
          {t("mains libres")}
        </span>
      ) : null}
    </span>
  );
}
